import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader } from 'lucide-react';

interface AuthRedirectProps {
  children: React.ReactNode;
}

export const AuthRedirect: React.FC<AuthRedirectProps> = ({ children }) => {
  const { user, profile, loading } = useAuth();
  const currentPath = window.location.pathname;

  // Debug logging for auth redirect
  console.log('[AuthRedirect] ======= AUTH REDIRECT DEBUG =======');
  console.log('[AuthRedirect] Current URL:', window.location.href);
  console.log('[AuthRedirect] Current path:', currentPath);
  console.log('[AuthRedirect] User:', user?.id);
  console.log('[AuthRedirect] Profile role:', profile?.role);
  console.log('[AuthRedirect] Email confirmed:', user?.email_confirmed_at);
  console.log('[AuthRedirect] Loading state:', loading);
  console.log('[AuthRedirect] ==========================================');

  // CRITICAL: Exclude ALL tenant portal routes from automatic redirection
  // This prevents interference with tenant invitation flows
  if (currentPath.startsWith('/tenant-portal')) {
    console.log('[AuthRedirect] Skipping redirect for tenant portal route:', currentPath);
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2 text-primary">
          <Loader className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // If user is authenticated and email is confirmed, redirect to their dashboard
  // But only redirect away from auth-specific pages, allow access to home page
  if (user && profile && user.email_confirmed_at) {
    const authPages = ['/login', '/signup', '/reset-password', '/tenant-portal/login', '/tenant-portal/signup'];
    const isAuthPage = authPages.some(page => currentPath.startsWith(page));
    
    if (isAuthPage) {
      const targetPath = profile.role === 'landlord' ? '/dashboard' : '/tenant-portal/dashboard';
      
      // Prevent double navigation - don't redirect if already on target path
      if (currentPath === targetPath) {
        console.log('[AuthRedirect] Already on target path, skipping redirect');
        return <>{children}</>;
      }
      
      console.log('[AuthRedirect] Redirecting authenticated user away from auth page to:', targetPath);
      return <Navigate to={targetPath} replace />;
    }
  }

  // If user is authenticated but email not confirmed, let them stay on auth pages
  // so they can access login/signup/email-confirmation pages

  return <>{children}</>;
};